import {
  flow,
  extend,
  reduce,
  get,
  omit,
  each,
  find,
  filter,
  map,
  isEmpty,
  pickBy,
  remove,
  countBy,
  maxBy,
} from 'lodash';
import { curriedMap, curriedFilter, splitEvery, propOr } from '@adjust/dashboard-core/lib/fp';

/**
 * Model for managing apps
 */
function App(Api, Account, Helper, $q, gettext, gettextCatalog) {
  'ngInject';
  var _skipped = [];
  var _currentApp;

  return {
    checkIntegration: checkIntegration,
    createApp: createApp,
    createBatchEventTypes: createBatchEventTypes,
    createEventType: createEventType,
    editEventType: editEventType,
    getApp: getApp,
    getAppIds: getAppIds,
    getApps: getApps,
    getAppsMultiAccount: getAppsMultiAccount,
    extendWithAccessDetails: extendWithAccessDetails,
    getConfiguredPlatforms: getConfiguredPlatforms,
    getCurrentApp: getCurrentApp,
    getCustomEventTypes: getCustomEventTypes,
    getEventTypes: getEventTypes,
    getEventCallbacks: getEventCallbacks,
    getFraudSettings: getFraudSettings,
    getSettings: getSettings,
    getSSRV: getSSRV,
    hasConfiguredIOSPlatform: hasConfiguredIOSPlatform,
    hasConfiguredPlatforms: hasConfiguredPlatforms,
    manageEventType: manageEventType,
    prepareSettings: prepareSettings,
    preparePlatformSettings: preparePlatformSettings,
    saveFraudSettings: saveFraudSettings,
    saveSettings: saveSettings,
    sendIntegrationEmail: sendIntegrationEmail,
    setCurrentApp: setCurrentApp,
    skipIntegration: skipIntegration,
    updateApp: updateApp,
    updatePlatformSettings: updatePlatformSettings,
    getRawExportSettings: getRawExportSettings,
    deleteRawExportSettings: deleteRawExportSettings,
    updateRawExportSettings: updateRawExportSettings,
    installStats: installStats,
    fetchDeviceData: fetchDeviceData,
    forgetDevice: forgetDevice,
    kpis: kpis,
    createSecret: createSecret,
    getSecrets: getSecrets,
    getSecretNames: getSecretNames,
    updateSecret: updateSecret,
    revokeSecret: revokeSecret,
    reactivateSecret: reactivateSecret,
    enforceSdkSignature: enforceSdkSignature,
    isMultiplatform: isMultiplatform,
    orderEventTypes: orderEventTypes,
    getUninstallSettings: getUninstallSettings,
    deleteUninstallSettings: deleteUninstallSettings,
    updateGoogleUninstallSettings: updateGoogleUninstallSettings,
    canUpdateEmailReports: canUpdateEmailReports,
    getMostActiveSdk: getMostActiveSdk,
    _extractMostActiveSdk: _extractMostActiveSdk,
    getPlatformIdPlaceholder: getPlatformIdPlaceholder,
    _getDefaultEventTypeNames: _getDefaultEventTypeNames
  };

  /**
   * @ngdoc method
   * @name kpis
   * @methodOf adjust.service:App
   * @description
   * Get multiple app kpis
   * break them down to chunks
   * and aggregate the results as one result
   *
   * @param {Array} appTokens tokens of the apps
   * @param {Object} params request params
   * @param {string} request can be 'billable'
   *
   * @returns {promise} A promise representing the state of the kpi requests
   */
  function kpis(appTokens, requestParams, request) {

    var endpoint = request === 'billable' ? 'billableKpiApps' : 'kpiApps';

    var promises = flow(splitEvery(200), curriedMap(tokens => {

      var params = extend({}, requestParams, {app_tokens: tokens.join(',')});

      return Api[endpoint].get(params).$promise;

    }))(appTokens);

    return $q.all(promises)
      .then(function(results) {

        return reduce(results, function(aggr, result) {

          if (!get(result, 'result_set.apps')) {
            result.result_set = {apps: []};
          }

          extend(aggr, omit(result, 'result_set'));

          aggr.result_set = angular.copy(aggr.result_set) || {apps: []};
          aggr.result_set.apps = aggr.result_set.apps.concat(result.result_set.apps);

          return aggr;

        }, {});

      });

  }

  /**
   * @ngdoc method
   * @name installStats
   * @methodOf adjust.service:App
   * @description
   * Get latest stats of the apps
   *
   * @param {Array} tokens tokens of the apps
   * @param {date} startDate start date
   * @param {date} endDate end date
   *
   * @returns {promise} A promise representing the state of the kpi requests
   */
  function installStats(tokens, startDate, endDate) {

    var params = {
      kpis: 'installs',
      grouping: 'apps,days',
      start_date: startDate,
      end_date: endDate
    };

    return $q.all([
      kpis(tokens, params),
      kpis(tokens, params, 'billable')
    ]);

  }

  /**
   * @ngdoc method
   * @name getApps
   * @methodOf adjust.service:App
   * @description
   * Get apps for the current user
   *
   * @returns {promise} A promise representing the state of the apps request
   */
  function getApps() {

    return Helper.useCache(Api.appsList.get, 'apps');

  }

  /**
   * @returns {Promise}
   */
  function getAppsMultiAccount() {

    return Helper.useCache(() => Api.appsList.get({multiview: 'multiview'}), 'appsMultiple');

  }

  /**
   * @ngdoc method
   * @name extendWithAccessDetails
   * @methodOf adjust.service:App
   *
   * @description
   * Get apps list extended with details about access
   *
   * @param {Array} apps list of apps
   * @param {Object} access access details
   * @returns {Array} apps list with extended details about access
   */
  function extendWithAccessDetails(apps, access) {

    var tmp = angular.copy(apps);

    each(access, function(accessDetails, token) {
      extend(find(tmp, {app_token: token}), {
        access: accessDetails
      });
    });

    return tmp;

  }

  /**
   * @ngdoc method
   * @name getSSRV
   * @methodOf adjust.service:App
   * @description
   * Upload purchase verification to server
   *
   * @returns {promise} A promise representing the state of the upload
   */
  function getSSRV(token) {

    return Api.dashboard.get({token: token, action: 'ssrv_settings'}).$promise;

  }

  /**
   * @ngdoc method
   * @name getAppIds
   * @methodOf adjust.service:App
   * @description
   * Get app ids used to fetch apptrace data for multiple apps
   *
   */
  function getAppIds() {

    return Helper.useCache(Api.appsList.get, 'apps')
      .then(function(result) {

        var ids = [];

        each(result.apps, function(app) {

          var numOfPlatforms = filter(app.platforms, {configured: true}).length;

          each(app.platforms, function(platform, platformName) {
            if (platform.configured) {
              ids.push({
                id: platform.app_id,
                name: (numOfPlatforms > 1) ? (app.name + ' ' + platformName) : app.name
              });
            }

          });
        });

        return ids;
      }, function(error) {
        return $q.reject(error);
      });

  }

  /**
   * @ngdoc method
   * @name getApp
   * @methodOf adjust.service:App
   * @description
   * Get app by token
   *
   * @param {string} token app's token
   * @returns {promise} A promise representing the state of the app request
   */
  function getApp(token) {

    return Api.dashboard.get({token: token}).$promise;

  }

  /**
   * @ngdoc method
   * @name setCurrentApp
   * @methodOf adjust.service:App
   * @description
   * Set current app for nested views
   *
   * @param {Object} app app to set as current
   */
  function setCurrentApp(app) {

    if (app.title) {
      app.name = app.title;
    }

    _currentApp = app;
  }

  /**
   * @ngdoc method
   * @name getCurrentApp
   * @methodOf adjust.service:App
   * @description
   * Get current app for nested view and ensure that it not empty
   *
   * @returns {Object} current app
   */
  function getCurrentApp() {

    if (!_currentApp) {
      throw new Error('There is no current app set the moment');
    }

    return _currentApp;

  }

  /**
   * @ngdoc method
   * @name createApp
   * @methodOf adjust.service:App
   * @description
   * Create new app
   *
   * @param {Object} params params of the new app
   * @returns {promise} A promise representing the state of the app create request
   */
  function createApp(params) {

    return Api.dashboard.create({}, params).$promise;

  }

  /**
   * @ngdoc method
   * @name hasConfiguredIOSPlatform
   * @methodOf adjust.service:App
   * @description
   * Check if app has configured ios platform
   *
   * @param params
   * @returns {*|string}
   */
  function hasConfiguredIOSPlatform(params) {

    return params.platforms && params.platforms.ios && params.platforms.ios.app_id;

  }

  /**
   * @ngdoc method
   * @name getApp
   * @methodOf adjust.service:App
   * @description
   * Update the app with provided parameters
   *
   * @param {string} token app's token
   * @param {Object} params params to update
   * @returns {promise} A promise representing the state of the app update request
   */
  function updateApp(token, params) {

    return Api.dashboard.update({token: token}, params).$promise;

  }

  function updatePlatformSettings(token, params) {

    return Api.dashboard.update({
      token: token,
      action: 'platform_settings'
    }, params).$promise;

  }


  /**
   * @ngdoc method
   * @name createEventType
   * @methodOf adjust.service:App
   * @description
   * Create event type for the app
   *
   * @param {string} token app's token
   * @param {string} name event's name
   * @returns {promise} A promise representing the state of the event type creation request
   */
  function createEventType(token, name) {

    var params = {name: name};

    return Api.dashboard.create({token: token, action: 'event_types'}, params).$promise;

  }

  /**
   * @ngdoc method
   * @name createBatchEventTypes
   * @methodOf adjust.service:App
   * @description
   * Create batch event types
   *
   * @param {string} token app's token
   * @param {Array} names array of names to create
   * @returns {promise} A promise representing the state of the event type creation request
   */
  function createBatchEventTypes(token, names) {

    names = map(names, function(name) {
      return {name: name.name};
    });

    var params = {event_types: names};

    return Api.dashboard.create({token: token, action: 'event_types', subAction: 'batch'}, params).$promise;

  }

  /**
   * @ngdoc method
   * @name manageEventType
   * @methodOf adjust.service:App
   * @description
   * Manage event type's state, archive it or restore it
   *
   * @param {string} token app's token
   * @param {string} action can be **archive** or **unarchive**
   * @param {number} id id of the event
   * @returns {promise} A promise representing the state of the event type update request
   */
  function manageEventType(token, action, id) {

    return Api.dashboard.patch({token: token, action: 'event_types', subAction: action, id: id}).$promise;

  }

  /**
   * @ngdoc method
   * @name editEventType
   * @methodOf adjust.service:App
   * @description
   * Edit event type's parameters
   *
   * @param {string} token app's token
   * @param {number} id id of the event
   * @param {Object} params parameters to be changed
   * @param {boolean} withoutCallback send to callback sub-action
   * @returns {promise} A promise representing the state of the event type update request
   */
  function editEventType(token, id, params, withoutCallback) {

    var useCallback = withoutCallback ? null : 'callback';
    return Api.dashboard.update({token: token, action: 'event_types', id: id, subAction: useCallback}, params).$promise;

  }

  /**
   * @ngdoc method
   * @name getEventTypes
   * @methodOf adjust.service:App
   * @description
   * Get event types of the app, without "General Revenue" event and auto-generated ones
   *
   * @param {string} token app's token
   * @param {boolean=} includeArchived include archived events
   * @returns {promise} A promise representing the state of the event types request
   *
   */
  function getEventTypes(token, includeArchived) {

    includeArchived = includeArchived || false;

    return Api.dashboard.get({token: token, action: 'event_types', include_archived: includeArchived}).$promise
      .then(function(result) {

        return filter(result.events, function(event) {
          return event.token !== '0' && !event.autogenerated;
        });

      });

  }

  /**
   * Get event callbacks
   *
   * @param {string} token app's token
   * @param {Object} packages account access to different packages
   * @param {Object=} features user access to different features
   * @param {Object=} app current loaded app
   * @returns {promise} A promise representing the state of the event callbacks request
   *
   */
  function getEventCallbacks(token, packages, features, app) {

    return Api.dashboard.query({token: token, action: 'callbacks'}).$promise
      .then(function(result) {
        return _extendEventTypes(result, packages, features, app);
      });

  }

  /**
   * @ngdoc method
   * @name getCustomEventTypes
   * @methodOf adjust.service:App
   * @description
   * Filter out custom event types from the event types response
   *
   * @param {string} token app's token
   * @returns {promise} A promise representing the state of the event types request
   */
  function getCustomEventTypes(token) {

    return Api.dashboard.get({token: token, action: 'event_types'}).$promise
      .then(function(result) {

        return filter(result.events, {autogenerated: false});

      });

  }

  /**
   * @param {String} token - app's token
   * @param {String} emails - emails which the integration details are sent to
   * @returns {Promise} A promise representing the state of the integration email request
   */
  function sendIntegrationEmail(token, emails) {

    return Api.setup.create({app_token: token, action: 'integration_email'}, {emails}).$promise;

  }

  /**
   * @ngdoc method
   * @name _getDefaultEventTypeNames
   * @methodOf adjust.service:App
   * @description
   * Get default (pseudo) event type names
   *
   * @param {Object} packages account access to different packages
   * @param {boolean=false} packages.fraud access to package
   * @param {boolean=false} packages.uninstall access to package
   * @param {Object=} features user access to different features
   * @param {Object=} features user access to different features
   * @returns {string[]} default event type names
   * @private
   */
  function _getDefaultEventTypeNames(packages = {}, features = {}, app = null) {
    let events = [
      {key: 'impression', label: gettext('event-type.impression')},
      {key: 'click', label: gettext('event-type.click')},
      {key: 'install', label: gettext('event-type.install')},
      {key: 'session', label: gettext('event-type.session')},
      {key: 'reattribution', label: gettext('event-type.reattribution')},
      {key: 'attribution_update', label: gettext('event-type.attribution_update')},
      {key: 'gdpr_forget_device', label: gettext('event-type.gdpr_forget_device')},
      {key: 'subscription', label: gettext('event-type.subscription')},
    ];

    if (app?.stores.ios) {
      events = events.concat([
        {key: 'att_consent', label: gettext('event-type.ios_opt_in')},
      ]);

      if (features.skadnetwork?.enabled) {
        events = events.concat([
          {key: 'sk_install', label: gettext('event-type.skadnetwork')},
        ]);
      }
    }

    if (packages.fraud) {
      events = events.concat([
        {key: 'rejected_install', label: gettext('event-type.rejected_install')},
        {key: 'rejected_reattribution', label: gettext('event-type.rejected_reattribution')}
      ]);
    }

    if (packages.uninstall) {
      events = events.concat([
        {key: 'uninstall', label: gettext('event-type.uninstall')},
        {key: 'reinstall', label: gettext('event-type.reinstall')},
        {key: 'reattribution_reinstall', label: gettext('event-type.reattribution_reinstall')}
      ]);
    }

    if (packages.multitouch) {
      events = events.concat([
        {key: 'san_impression', label: gettext('event-type.san_impression')},
        {key: 'san_click', label: gettext('event-type.san_click')}
      ]);
    }

    if (features.cost_data && features.cost_data.enabled) {
      events = events.concat([
        {key: 'cost_update', label: gettext('event-type.cost_update')}
      ]);
    }

    if (packages.ad_revenue && features.revenue_data && features.revenue_data.enabled) {
      events = events.concat([
        {key: 'ad_revenue', label: gettext('event-type.ad_revenue')}
      ]);
    }

    return events;
  }

  /**
   * Extend list of custom events with predefined list of default events
   *
   * @param {Array} events events to merge with default ones
   * @param {Object} packages account access to different packages
   * @param {Object=} features user access to different features
   * @returns {Object} global and other callbacks result
   * @private
   */
  function _extendEventTypes(events, packages, features, app) {
    let defaultEvents = _getDefaultEventTypeNames(packages, features, app);
    let defaultEventKeys = map(defaultEvents, 'key');
    let callbacks = flow(
      curriedFilter(event => defaultEventKeys.includes(event.id) || event.custom),
      curriedMap(event => {

        const defaultEvent = find(defaultEvents, {key: event.id});

        if (!event.custom) {
          return extend(event, {
            name: gettextCatalog.getString(defaultEvent.label),
            token: event.id,
            idx: defaultEventKeys.indexOf(event.id)
          });
        }

        return event;

      })
    )(events);

    return {
      callbacks: callbacks.map(({ id, ...rest }) => ({
        id,
        subCallbacks: _getSubCallbacks(events, id),
        ...rest,
      })),
      global: { ...find(events, { id: 'global' }), subCallbacks: [] },
    };

  }

  function _getSubCallbacks(events, parentId) {
    const availableParentIds = ['subscription'];

    return availableParentIds.includes(parentId)
      ? events.filter(event => String(event.id).startsWith(`${parentId}_`))
      : [];
  }

  /**
   * @ngdoc method
   * @name _isSkipped
   * @methodOf adjust.service:App
   * @description
   * Check if sdk integration for the app is skipped
   *
   * @param {string} token app's token
   * @returns {boolean} is skipped already
   * @private
   */
  function _isSkipped(token) {

    return _skipped.includes(token);

  }

  /**
   * @ngdoc method
   * @name skipIntegration
   * @methodOf adjust.service:App
   * @description
   * Skip the sdk integration for the app
   *
   * @param {string} token app's token
   */
  function skipIntegration(token) {

    if (_isSkipped(token)) { return; }

    _skipped.push(token);

  }

  /**
   * @ngdoc method
   * @name checkIntegration
   * @methodOf adjust.service:App
   * @description
   * Check if sdk integration is done or skipped
   *
   * @param {Object} app app against which the sdk integration is checked
   * @returns {boolean} if integration is done or skipped
   */
  function checkIntegration(app) {

    return isEmpty(app.integration_dates) && !_isSkipped(app.token);

  }

  /**
   * @ngdoc method
   * @name getSettings
   * @methodOf adjust.service:App
   * @description
   * Get app settings
   *
   * @param {string} token app's token
   * @param {string} settings type of settings to get
   * @param {string=} mappings additional parameters for settings mapping
   * @returns {promise} A promise representing the state of the settings request
   */
  function getSettings(token, settings, mappings) {

    var params = {appToken: token, settings: settings};

    if (mappings) {
      params.mappings = mappings.join(',');
    }

    return Api.partnerSettings.get(params).$promise;

  }

  /**
   * @ngdoc method
   * @name saveSettings
   * @methodOf adjust.service:App
   * @description
   * Save app settings
   *
   * @param {string} token app's token
   * @param {string} settings type of settings to save
   * @param {Object} params parameters to change
   * @returns {promise} A promise representing the state of the save settings request
   */
  function saveSettings(token, settings, params) {

    return Api.partnerSettings.post({appToken: token, settings: settings}, params).$promise;

  }

  /**
   * @ngdoc method
   * @name getFraudSettings
   * @methodOf adjust.service:App
   * @description
   * Get fraud settings
   *
   * @param {string} token app's token
   * @returns {promise} A promise representing the state of the save settings request
   */
  function getFraudSettings(token) {

    return Api.fraudPreventionSettings.get({appToken: token}).$promise
      .then(function(result) {
        result.distribution_filter = result.distribution_filter || 'standard';
        return result;
      });
  }

  /**
   * @ngdoc method
   * @name saveFraudSettings
   * @methodOf adjust.service:App
   * @description
   * Save fraud settings
   *
   * @param {string} token app's token
   * @param {Object} params parameters to change
   * @returns {promise} A promise representing the state of the save settings request
   */
  function saveFraudSettings(token, params) {

    return Api.fraudPreventionSettings.update({appToken: token}, params).$promise;

  }

  /**
   * @ngdoc method
   * @name hasConfiguredPlatforms
   * @methodOf adjust.service:App
   * @description
   * Check if app has at least one configured platform
   *
   * @param {Object} app app's object
   * @returns {boolean} has platform(s)
   */
  function hasConfiguredPlatforms(app) {

    return filter(app.platforms, {configured: true}).length > 0;

  }

  /**
   * @ngdoc method
   * @name prepareSettings
   * @methodOf adjust.service:App
   * @description
   * Expose certain paramateres for the platform settings form
   *
   * @param {Object} app app details
   * @returns {Object} exposed settings object
   */
  function prepareSettings(app) {

    var settings = {};

    settings.name = app.name;
    settings.apple_provider_id = app.apple_provider_id;
    settings.default_platform = app.default_platform || 'android';
    settings.default_device_type = app.default_device_type;
    settings.platforms = angular.copy(app.platforms) || {};

    settings.ios_bundle_id = app.ios_bundle_id;
    settings.ios_team_id = app.ios_team_id;
    settings.app_scheme = app.app_scheme;

    return settings;

  }

  function preparePlatformSettings(app) {

    var settings = {};

    settings.name = app.name;
    settings.default_platform = app.default_platform || 'android';
    settings.platforms = angular.copy(app.platforms) || {};

    return settings;

  }

  /**
   * @ngdoc method
   * @name getConfiguredPlatforms
   * @methodOf adjust.service:App
   *
   * @description
   * Get list of configured platforms
   *
   * @param {Object} platforms platforms key-value pairs, where key is platform name, and value is platform details
   * @param {boolean=false} withDetails return array of platforms with full platform details
   * @returns {Array} array of configured platforms
   */
  function getConfiguredPlatforms(platforms, withDetails) {

    withDetails = withDetails || false;

    var configured = [];

    if (withDetails) {
      each(platforms, function(platform, key) {
        if (platform.configured) {
          platform.key = key;
          configured.push(platform);
        }
      });
    } else {
      configured = Object.keys(pickBy(platforms, function(platform) { return platform.configured; }));
    }

    return _sortConfiguredPlatforms(configured);

  }

  /**
   * @ngdoc method
   * @name getRawExportSettings
   * @methodOf adjust.service:App
   *
   * @description
   * Get raw export settings
   *
   * @param {string} appToken app token
   * @returns {promise} A promise representing the state of the settings get request
   */
  function getRawExportSettings(appToken) {

    return Api.settings.get({
      appToken: appToken,
      settings: 'raw_export_settings'
    }).$promise;

  }

  /**
   * @ngdoc method
   * @name deleteRawExportSettings
   * @methodOf adjust.service:App
   *
   * @description
   * Delete raw export settings
   *
   * @param {string} appToken app token
   * @returns {promise} A promise representing the state of the settings delete request
   */
  function deleteRawExportSettings(appToken) {

    return Api.settings.delete({
      appToken: appToken,
      settings: 'raw_export_settings'
    }).$promise;

  }

  /**
   * @ngdoc method
   * @name updateRawExportSettings
   * @methodOf adjust.service:App
   *
   * @description
   * Get raw export settings
   *
   * @param {string} appToken app token
   * @param {Object} settings basic settings, like credentials and csv definition
   * @param {Array} eventTypes array of selected events to export
   * @param {Object} packages account access to different packages
   * @param {Object=} features user access to different features
   * @param {Object=} app current app
   * @returns {promise} A promise representing the state of the settings update request
   */
  function updateRawExportSettings(appToken, settings, eventTypes, packages, features, app) {

    var params = _prepareForRawSettings(settings, eventTypes, packages, features, app);

    return Api.settings.update({
      appToken: appToken,
      settings: 'raw_export_settings'
    }, params).$promise;

  }

  /**
   * @ngdoc method
   * @name _prepareForRawSettings
   * @methodOf adjust.service:App
   *
   * @description
   * Prepare settings for raw data settings save
   *
   * @param {Object} settings basic settings, like credentials and csv definition
   * @param {Array} eventTypes array of selected events to export
   * @param {Object} packages account access to different packages
   * @param {Object=} features user access to different features
   * @param {Object=} app current app
   * @returns {Object} prepared settings object
   * @private
   */
  function _prepareForRawSettings(settings, eventTypes, packages, features, app) {

    var events = map(filter(eventTypes, {selected: true}), 'token');
    var autoGenerated = map(_getDefaultEventTypeNames(packages, features, app), 'key');
    var selectedAutoGenerated = remove(events, function(event) {
      return autoGenerated.includes(event);
    });
    var autoGeneratedObj = {};

    each(autoGenerated, function(event) {
      autoGeneratedObj[event] = selectedAutoGenerated.includes(event);
    });

    return {
      raw_export_settings: {
        enabled: settings.enabled,
        credentials: settings.credentials,
        csv_definition: settings.csv_definition,
        activities: extend({
          events: events
        }, autoGeneratedObj)
      }
    };

  }

  /**
   * @ngdoc method
   * @name _sortConfiguredPlatforms
   * @methodOf adjust.service:App
   *
   * @description
   * Sorts the configured platforms to ensure they match the order of the stores constant
   *
   * @param {Array} configuredPlatforms
   * @returns {Array} sortedPlatforms
   * @private
   */
  function _sortConfiguredPlatforms(platforms) {

    return (platforms || []).sort(function(a, b) {

      if (a === 'windows' && b === 'windows-phone') {
        return 1;
      }

      return 0;

    });

  }

  /**
   * @ngdoc method
   * @name fetchDeviceData
   * @methodOf adjust.service:App
   * @description
   * Fetch device data
   *
   * @param {string} appToken
   * @param {string} advertisingId
   * @returns {promise} A promise representing the state of the device request
   */
  function fetchDeviceData(appToken, advertisingId) {

    return Api.device.query({app_token: appToken, advertising_id: advertisingId}).$promise;

  }

  /**
   * @ngdoc method
   * @name forgetDevice
   * @methodOf adjust.service:App
   * @description
   * Forget device data
   *
   * @param {string} appToken
   * @param {string} adId ad id
   * @returns {promise} A promise representing the state of the device request
   */
  function forgetDevice(appToken, adId) {

    return Api.device.delete({app_token: appToken, action: 'forget_device', adid: adId}).$promise;

  }

  /**
   * @ngdoc method
   * @name createSecret
   * @methodOf adjust.service:App
   * @description
   * Create a secret
   *
   * @param {string} appToken
   * @param {Object} params
   * @returns {promise} A promise representing the state of the secrets request
   */
  function createSecret(appToken) {

    return Api.secrets.create({appToken: appToken}).$promise;

  }

  /**
   * @ngdoc method
   * @name getSecrets
   * @methodOf adjust.service:App
   * @description
   * List the app secrets
   *
   * @param {string} appToken
   * @returns {promise} A promise representing the state of the secrets request
   */
  function getSecrets(appToken) {

    return Api.secrets.query({appToken: appToken}).$promise;

  }

  /**
   * @ngdoc method
   * @name getSecretNames
   * @methodOf adjust.service:App
   * @description
   * List the app secret names indexed by version number
   *
   * @param {string} appToken
   * @returns {promise} A promise representing the state of the secrets request
   */
  function getSecretNames(appToken) {

    return getSecrets(appToken)
      .then(function(result) {

        const secretNames = reduce(
          result.secrets,
          (names, secret) => ({
            ...names,
            [secret.version]: secret.name
          }), {}
        );

        return {
          ...secretNames,
          '-1': 'Invalid Secret',
          '0': 'No Secret'
        };

      });

  }

  /**
   * @ngdoc method
   * @name updateSecret
   * @methodOf adjust.service:App
   * @description
   * Update the app secret
   *
   * @param {string} appToken
   * @param {string} secretId
   * @param {Object} params
   * @returns {promise} A promise representing the state of the update secret request
   */
  function updateSecret(appToken, secretId, params) {

    return Api.secrets.update({appToken: appToken, id: secretId}, params).$promise;

  }

  /**
   * @ngdoc method
   * @name revokeSecret
   * @methodOf adjust.service:App
   * @description
   * Revoke the app secret
   *
   * @param {string} appToken
   * @param {string} secretId
   * @returns {promise} A promise representing the state of the revoke secret request
   */
  function revokeSecret(appToken, secretId) {

    return Api.secrets.update({appToken: appToken, id: secretId, action: 'revoke'}).$promise;

  }

  /**
   * @ngdoc method
   * @name reactivateSecret
   * @methodOf adjust.service:App
   * @description
   * Reactivate the app secret
   *
   * @param {string} appToken
   * @param {string} secretId
   * @returns {promise} A promise representing the state of the reactivating secret request
   */
  function reactivateSecret(appToken, secretId) {

    return Api.secrets.update({appToken: appToken, id: secretId, action: 'reactivate'}).$promise;

  }

  /**
   * @ngdoc method
   * @name enforceSdkSignature
   * @methodOf adjust.service:App
   * @description
   * Enforce the signed installs for app
   *
   * @param {string} appToken
   * @param {boolean} enforceState
   * @returns {promise} A promise representing the state of the enforcing signed installs
   */
  function enforceSdkSignature(appToken, state) {

    return Api.dashboard.update({token: appToken, action: 'update_sdk_signature_settings'}, {
      'enforce_install_signing': state
    }).$promise;

  }

  /**
   * @ngdoc method
   * @name isMultiplatform
   * @methodOf adjust.service:App
   * @description
   * Checks if the provided app is multi platform
   *
   * @param {Object} app
   * @returns {boolean}
   */
  function isMultiplatform(app) {

    return countBy(map(app.platforms, 'configured')).true > 1;

  }

  /**
   * @ngdoc method
   * @name orderEventTypes
   * @methodOf adjust.service:App
   *
   * @description
   * Order event types by name, but put auto-generated (default) on top
   *
   * @param {Object} event event to order
   * @returns {string} order predicate
   */
  function orderEventTypes(event) {

    if (!event.custom) {
      return event.idx;
    }

    return event.name;

  }

  /**
   * @ngdoc method
   * @name getUninstallSettings
   * @methodOf adjust.service:App
   *
   * @param {string} platform
   * @param {string} appToken
   * @returns {promise}
   */
  function getUninstallSettings(platform, appToken) {

    return Api.dashboard.get({
      token: appToken,
      action: platform,
      subAction: 'uninstall_settings'
    }).$promise;

  }

  /**
   * @ngdoc method
   * @name updateGoogleUninstallSettings
   * @methodOf adjust.service:App
   *
   * @param {string} platform
   * @param {Object} payload
   * @returns {promise}
   */
  function updateGoogleUninstallSettings(appToken, payload) {

    return Api.dashboard.update({
      token: appToken,
      action: 'google',
      subAction: 'uninstall_settings'
    }, payload).$promise;

  }

  /**
   * @ngdoc method
   * @name deleteUninstallSettings
   * @methodOf adjust.service:App
   *
   * @param {string} platform
   * @param {string} appToken
   * @returns {promise}
   */
  function deleteUninstallSettings(platform, appToken) {

    return Api.dashboard.remove({
      token: appToken,
      action: platform,
      subAction: 'uninstall_settings'
    }).$promise;

  }

  /**
   * @param {Object} app
   * @param {Object} account
   * @param {Object} user
   * @returns {Boolean}
   */
  function canUpdateEmailReports(app, account, user) {

    if (!app.start_date) {
      return false;
    }

    if (Account.hasFeature(account, 'revenue_data') && !get(user, 'features.revenue_data.enabled')) {
      return false;
    }

    return get(app, 'permissions.update_settings', false);

  }

  /**
   * Fetch the most active sdk version from kpi-service or local-storage
   *
   * @param {String} appToken
   * @returns {Promise}
   */
  function getMostActiveSdk(appToken) {
    const yesterday = moment()
      .utc()
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    const today = moment()
      .utc()
      .format('YYYY-MM-DD');
    const grouping = ['os_names', 'sdk_versions'].join(',');
    const params = {
      appToken,
      grouping,
      kpis: ['installs_with_valid_signature', 'installs_with_invalid_signature'].join(','),
      start_date: yesterday,
      end_date: today,
    };

    return Api.sdkSecret.get(params).$promise.then(_extractMostActiveSdk);
  }

  /**
   * Transform the kpi service response to extract platfrom name and sdk version
   *
   * @param {Object} kpiResponse
   * @returns {Array}
   */
  function _extractMostActiveSdk(kpiResponse) {
    return flow(propOr([], 'result_set.os_names'), curriedMap(sdk => ({
      name: sdk.os_name,
      version: _extractSdkVersion(sdk)
    })))(kpiResponse);
  }

  /**
   * Extract the version with greater kpi_values
   *
   * @param {Object} sdkData
   * @returns {Number}
   */
  function _extractSdkVersion(sdkData = {}) {
    const { sdk_versions = [] } = sdkData;
    const mostActiveSdk = maxBy(
      sdk_versions,
      version => version.kpi_values.reduce((sum, value) => sum + value, 0)
    );
    return get(mostActiveSdk, 'sdk_version', null);
  }

  function getPlatformIdPlaceholder(platform, store) {
    const placeholder = {
      ios: '123456789',
      android: {
        google: 'com.your.app',
        amazon: 'B0123ABCDE'
      },
      'windows-phone': '1abcdefghi345',
      windows: '9zyxwvutsrqp'
    };
    const getter = {
      ios: [platform],
      android: [platform, store],
      'windows-phone': [platform],
      windows: [platform]
    }[platform];

    return get(placeholder, getter, '');
  }
}

export { App };
